import { useEffect, useState } from 'react';
import { api } from '../api/client';

interface Project {
  id: string;
  name: string;
  description: string;
  agents: number;
  status: 'active' | 'completed' | 'archived';
  updatedAt: string;
}

export default function Projects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchProjects = async () => {
      try {
        const response = await api.get('/api/v1/projects');
        if (!cancelled) {
          const data = response.data;
          setProjects(Array.isArray(data) ? data : data.projects || []);
        }
      } catch {
        if (!cancelled) {
          setProjects([
            { id: '1', name: 'Q4 Market Analysis', description: 'Comprehensive market research', agents: 3, status: 'active', updatedAt: '2 hours ago' },
            { id: '2', name: 'Product Roadmap', description: '2024 product planning', agents: 2, status: 'active', updatedAt: '1 day ago' },
            { id: '3', name: 'Competitor Research', description: 'Competitive landscape analysis', agents: 4, status: 'completed', updatedAt: '3 days ago' },
          ]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchProjects();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!showModal) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowModal(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showModal]);

  const handleCreateProject = async () => {
    if (!newProject.name.trim()) return;
    setCreating(true);
    try {
      const response = await api.post('/api/v1/projects', {
        name: newProject.name.trim(),
        description: newProject.description.trim() || 'No description',
      });
      const created = response.data;
      setProjects(prev => [
        ...prev,
        {
          id: created.id || String(Date.now()),
          name: created.name || newProject.name.trim(),
          description: created.description || newProject.description.trim() || 'No description',
          agents: created.agents ?? 0,
          status: created.status || 'active',
          updatedAt: created.updatedAt || 'Just now',
        },
      ]);
    } catch {
      // API failed — add locally as fallback
      setProjects(prev => [
        ...prev,
        {
          id: String(Date.now()),
          name: newProject.name.trim(),
          description: newProject.description.trim() || 'No description',
          agents: 0,
          status: 'active',
          updatedAt: 'Just now',
        },
      ]);
    } finally {
      setNewProject({ name: '', description: '' });
      setCreating(false);
      setShowModal(false);
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!confirm('Are you sure you want to delete this project?')) return;
    try {
      await api.delete(`/api/v1/projects/${projectId}`);
    } catch {
      // local removal only
    }
    setProjects(prev => prev.filter(p => p.id !== projectId));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'completed': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'archived': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Projects</h1>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          + New Project
        </button>
      </div>

      <div className="space-y-4">
        {projects.map((project) => (
          <div
            key={project.id}
            className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100">{project.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{project.description}</p>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(project.status)}`}>
                {project.status}
              </span>
              <button
                onClick={() => handleDeleteProject(project.id)}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                title="Delete project"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
            <div className="mt-4 flex items-center justify-between text-sm text-gray-400 dark:text-gray-500">
              <span>{project.agents} agents assigned</span>
              <span>Updated {project.updatedAt}</span>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="project-modal-title"
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
            <h2 id="project-modal-title" className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">Create New Project</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project Name</label>
                <input
                  type="text"
                  value={newProject.name}
                  onChange={(e) => setNewProject(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Q4 Market Analysis"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief project description"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowModal(false); setNewProject({ name: '', description: '' }); }}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateProject}
                disabled={!newProject.name.trim() || creating}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {creating && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                Create Project
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
